import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import cn from 'classnames';
import { isAfter, startOfDay } from 'date-fns';
import { compact } from 'lodash-es';
import { InvoicePaymentFailureCode } from 'anchor-scout-common/types';
import { Button, Callout, Typography } from 'seagull';
import { useOverlayContext, useStoreActions, useStore, useIoContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise } from '~/infra/utils';
import { invoiceModel, relationshipModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { helpArticles } from '~/utils/helpArticles';
import ChangesApprovalInfoBox from '../ChangesApprovalInfoBox/ChangesApprovalInfoBox';
import ExternalLinkButton from '../ExternalLinkButton';
import styles from './FullPageInvoice.module.scss';
export const calloutTextsPath = 'components.fullPageInvoice.callout';
export function FullPageInvoiceCallout(props) {
    const { id, text, breakOnNewLines, intent, actions, footer } = props;
    return /*#__PURE__*/ _jsx(Callout, {
        intent: intent,
        testId: "fpi-".concat(id),
        footer: footer,
        withAction: !!(actions === null || actions === void 0 ? void 0 : actions.length) && /*#__PURE__*/ _jsx(_Fragment, {
            children: actions.map((a)=>/*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'invoice_callout_action',
                                data: {
                                    callout_id: id
                                }
                            },
                            intent: a.intent,
                            skin: a.skin,
                            disabled: a.disabled,
                            tooltip: a.tooltipTitle ? {
                                title: /*#__PURE__*/ _jsx("div", {
                                    "data-testid": "fpi-callout-tooltip",
                                    children: a.tooltipTitle
                                }),
                                placement: 'top'
                            } : undefined,
                            loading: a.loading,
                            testId: "fpi-".concat(a.id),
                            onClick: a.onClick,
                            children: a.text
                        }),
                        a.errorMsg && /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "error",
                            testId: "fpi-".concat(a.id, "-error"),
                            children: a.errorMsg
                        })
                    ]
                }, id))
        }),
        children: /*#__PURE__*/ _jsx("div", {
            className: cn(breakOnNewLines && styles.breakOnNewLines),
            children: text
        })
    });
}
function showLearnAboutHandlingChargebacks(invoice, isVendor) {
    const failureCode = invoiceModel.paymentFailureCode(invoice);
    return isVendor && failureCode && [
        InvoicePaymentFailureCode.DisputeProductUnacceptable,
        InvoicePaymentFailureCode.DisputeCreditNotProcessed
    ].includes(failureCode);
}
export function getFullPageInvoiceCallouts(props, texts) {
    const { invoice, relationship, isVendor, disabling, now, onPayNow, onRetryCharge, onResumePayment, onApprovePayment, onDisableAutoApprove } = props;
    const isRefundRequested = invoiceModel.isRefundRequested(invoice);
    const isRefundFailed = invoiceModel.isRefundFailed(invoice);
    const hasPaymentMethod = relationshipModel.paymentMethod(relationship) != null;
    const customerName = relationshipModel.customerName(relationship);
    const sellerName = relationshipModel.sellerFullName(relationship);
    const isTerminated = relationshipModel.isTerminated(relationship);
    const autoApprovalDate = invoiceModel.autoApprovalDate(invoice);
    const paymentProcessingDate = invoiceModel.paymentProcessingDate(invoice);
    const disputedMessage = invoiceModel.disputedMessage(invoice);
    return compact([
        invoiceModel.isPaymentFailed(invoice) && {
            id: 'payment-failed',
            text: /*#__PURE__*/ _jsxs(Typography, {
                testId: "payment-failed-text",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        as: "span",
                        weight: "normal",
                        children: texts.paymentFailedLabel()
                    }),
                    ' ',
                    invoiceModel.paymentFailureMessage(invoice) || '',
                    ' ',
                    showLearnAboutHandlingChargebacks(invoice, isVendor) && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                bi: {
                                    name: 'invoice_payment_failed_chargeback_learn_more'
                                },
                                testId: "payment-failed-disputed-learn-more",
                                openInNewTab: true,
                                href: helpArticles.chargebacksAndDisputes('missing-auth'),
                                children: texts.chargebackInfo()
                            }),
                            ' ',
                            texts.chargebackSuffix()
                        ]
                    })
                ]
            }),
            intent: 'warning',
            actions: [
                {
                    id: 'retry-charge',
                    skin: 'primaryOutline',
                    text: texts.retryCharge(),
                    intent: Intent.InvoiceRetryCharge,
                    disabled: isTerminated || !invoice.isRetryable,
                    tooltipTitle: isTerminated ? texts.terminated() : !invoice.isRetryable ? texts.contactSupportRetry() : undefined,
                    onClick: onRetryCharge
                }
            ]
        },
        invoiceModel.isRequiresPaymentApproval(invoice) && {
            id: 'requires-approval',
            text: isVendor ? texts.requiresApprovalVendor() : texts.requiresApprovalClient(),
            actions: compact([
                !isVendor && {
                    id: 'approve-payment',
                    text: texts.approvePayment(),
                    intent: Intent.InvoiceApprovePayment,
                    disabled: isTerminated,
                    tooltipTitle: isTerminated ? texts.terminated() : undefined,
                    onClick: onApprovePayment
                }
            ]),
            footer: autoApprovalDate && /*#__PURE__*/ _jsx(ChangesApprovalInfoBox, {
                testId: "fpi-auto-approval",
                onDisable: async ()=>onDisableAutoApprove(invoice),
                disabling: disabling,
                autoApprovalDate: autoApprovalDate,
                showDisableButton: !isVendor
            })
        },
        disputedMessage != null && {
            id: 'payment-disputed',
            intent: 'warning',
            text: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        as: "span",
                        weight: "normal",
                        children: texts.paymentDisputedLabel()
                    }),
                    ' ',
                    disputedMessage,
                    ' ',
                    /*#__PURE__*/ _jsx(ExternalLinkButton, {
                        bi: {
                            name: 'invoice_payment_disputed_learn_more'
                        },
                        testId: "payment-disputed-learn-more",
                        openInNewTab: true,
                        href: helpArticles.chargebacksAndDisputes(),
                        children: texts.chargebackInfo()
                    }),
                    ' ',
                    texts.chargebackSuffix()
                ]
            })
        },
        invoiceModel.isPending(invoice) && invoice.netTerms > 0 && {
            id: 'future-payment',
            text: texts.futurePayment({
                date: paymentProcessingDate
            }),
            actions: compact([
                !isVendor && hasPaymentMethod && isAfter(startOfDay(new Date(paymentProcessingDate)), startOfDay(now)) && {
                    id: 'pay-now',
                    text: texts.payNow(),
                    intent: Intent.InvoicePayNow,
                    disabled: isTerminated,
                    tooltipTitle: isTerminated ? texts.terminated() : undefined,
                    onClick: onPayNow
                }
            ])
        },
        invoiceModel.isPaymentStopped(invoice) && {
            id: 'payment-stopped',
            text: isVendor ? texts.paymentStoppedVendor({
                customerName,
                reason: invoiceModel.paymentStopReason(invoice)
            }) : texts.paymentStoppedClient(),
            breakOnNewLines: true,
            intent: isVendor ? 'warning' : 'info',
            actions: compact([
                !isVendor && {
                    id: 'resume-payment',
                    text: texts.resumePayment(),
                    intent: Intent.InvoiceResumePayment,
                    disabled: isTerminated,
                    tooltipTitle: isTerminated ? texts.terminated() : undefined,
                    onClick: onResumePayment
                }
            ])
        },
        isRefundRequested && {
            id: 'refund-requested',
            text: texts.refundRequested(),
            intent: 'warning'
        },
        isRefundFailed && {
            id: 'refund-failed',
            text: texts.refundFailed(),
            intent: 'warning'
        },
        invoiceModel.isPartiallyRefunded(invoice) && {
            id: 'partially-refunded',
            text: texts.partiallyRefunded({
                amount: invoiceModel.totalRefunded(invoice),
                feeNote: invoiceModel.hasRefundFees(invoice) ? texts.partiallyRefundedFees() : '',
                sellerName
            }),
            intent: 'info'
        }
    ]);
}
export function ResolvePaymentCallout(props) {
    const { invoice, isVendor, reloadInvoice } = props;
    const { stripeUrl, clientSecret } = invoiceModel.processingAction(invoice);
    const { logBI } = useStoreActions();
    const { t: calloutTexts } = useLocaleKeys(calloutTextsPath);
    const { browser: { openNewTab }, reactStripe: { useStripe } } = useIoContext();
    const stripe = useStripe();
    const overlayContext = useOverlayContext();
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const impersonatingAs = useStore(selectors.impersonatingAs);
    async function onResolvePayment(param) {
        let { stripeUrl, clientSecret } = param;
        logBI(10590, {
            invoiceId: invoice.id
        });
        if (stripeUrl != null) {
            openNewTab(stripeUrl);
            document.addEventListener('visibilitychange', listener);
        } else if (stripe != null && clientSecret != null) {
            setLoading(true);
            setErrorMsg('');
            const { error } = await stripe.confirmCardPayment(clientSecret);
            await handlePromise(reloadInvoice());
            if (error) {
                setErrorMsg(error.message || 'An error occurred');
            }
            setLoading(false);
        }
        async function listener() {
            if (document.visibilityState === 'visible') {
                document.removeEventListener('visibilitychange', listener);
                overlayContext.startLoader();
                await handlePromise(reloadInvoice());
                overlayContext.stopLoader();
            }
        }
    }
    return /*#__PURE__*/ _jsx(FullPageInvoiceCallout, {
        id: "payment-verification",
        text: /*#__PURE__*/ _jsxs("div", {
            children: [
                calloutTexts.resolvePaymentCCAuth({
                    party: isVendor ? 'your client' : 'you'
                }),
                errorMsg && /*#__PURE__*/ _jsx(Typography, {
                    testId: "fpi-resolve-payment-error",
                    skin: "error",
                    children: errorMsg
                })
            ]
        }),
        actions: compact([
            (stripeUrl != null || clientSecret != null) && {
                id: 'resolve-payment',
                text: calloutTexts.resolvePayment(),
                intent: Intent.InvoiceResolvePayment,
                loading,
                tooltipTitle: isVendor ? calloutTexts.onlyClientCanComplete() : impersonatingAs ? calloutTexts.disabledWhileImpersonating() : undefined,
                onClick: ()=>onResolvePayment({
                        stripeUrl,
                        clientSecret
                    }),
                disabled: isVendor || !!impersonatingAs
            }
        ])
    });
}
